import React, {useEffect, useRef, useState} from "react";
import makeStyles from "@mui/styles/makeStyles";
import {useTranslation} from "react-i18next";
import config from "./../app/config";
import api from "../app/api";
import API from "../app/api";
import {Auth, LocalStorage} from "./auth";
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import {IconButton, TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import {useHistory} from "react-router-dom";
import _ from "lodash";

const useStyles = makeStyles((theme) => {
    return {
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
        },
        btn: {
            color: theme.palette.login.btnText,
            borderColor: theme.palette.login.text,
            background: theme.palette.default.main,
        },
        lnkbtn: {
            color: theme.palette.login.text,
        },
        page: {
            display: "grid",
            alignItems: "center",
            justifyContent: "center",
            "-ms-flex-align": "center",
            "-ms-flex-pack": "center",
            height: "100vh",
            marginLeft: "auto",
            marginRight: "auto",
            paddingLeft: "15px",
            paddingRight: "15px",
            background: theme.palette.background.default,
        },
        container: {
            width: "100%",
            maxWidth: 350,
            borderRadius: "5px",
            backgroundColor: theme.palette.background.paper,
            boxShadow: "1px 2px 4px 0 " + theme.palette.login.border,
            border: "1px solid " + theme.palette.login.border,
            padding: "20px",
            maxHeight: "100%",
        },
        logo: {
            textAlign: "center",
            paddingTop: "40px",
            paddingBottom: "30px",
            "& img": {
                width: "160px",
            },
        },
        content: {
            color: theme.palette.default.contrastText,
        },
        contentText: {
            textAlign: "center",
            fontSize: "1.4rem",
            fontWeight: "600",
            color: theme.palette.login.text,
        },
        inputStyle: {
            color: theme.palette.default.contrastText,
            backgroundColor: theme.palette.background.default,
            "&.MuiFormControl-root label.MuiFormLabel-root": {
                color: theme.palette.login.text,
                "&.Mui-focused": {
                    color: theme.palette.login.text,
                },
            },
            // focused color for input with variant='standard'
            "&.MuiFormControl-root .MuiInput-underline:after": {
                borderBottomColor: theme.palette.login.text,
            },
            // focused color for input with variant='filled'
            "&.MuiFormControl-root .MuiFilledInput-underline:after": {
                borderBottomColor: theme.palette.login.text,
            },
            // focused color for input with variant='outlined'
            "&.MuiFormControl-root fieldset": {
                borderColor: theme.palette.login.text,
                "&.MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.login.text,
                },
            },
            "&.MuiFormControl-root.Mui-focused fieldset": {
                borderColor: theme.palette.login.text,
                "&.MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.login.text,
                },
            },
        },
        footer: {
            paddingTop: "12px",
            textAlign: "center",
            color: theme.palette.login.footer,
            opacity: "0.3",
        },
        error: {
            color: theme.palette.error.light,
            fontWeight: "bold",
            textAlign: 'center',
            minHeight: 30,
        },
    };
});

const modes = {
    password: "password",
    pin: "pin",
};

const auth = new Auth();
const storage = new LocalStorage(localStorage);
const deviceId = storage.getDeviceId().deviceId;

function LoginApp() {
    const classes = useStyles();
    const {t} = useTranslation();
    let history = useHistory();
    const passwordRef = useRef(null);
    const userNameRef = useRef(null);

    const [loginStatus, setLoginStatus] = useState({
        authorized: false,
        token: null,
        refreshToken: null,
        error: null,
        errorDescription: null,
    });

    const [mode, setMode] = useState(modes.password);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isTrusted, setIsTrusted] = useState(false);
    const [pin, setPin] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [loginInProgress, setLoginInProgress] = useState(false);
    const [preventLogin, setPreventLogin] = useState(false);

    useEffect(async () => {
        await API.getMaintenance()
            .then(response => {
                if (response.data) {
                    const maintenance = response?.data[0]?.data;
                    setPreventLogin(maintenance?.maintenance);
                } else {
                    setPreventLogin(false);
                }
            }).catch(err => {
                console.error("getMaintenance error", err);
                setPreventLogin(false);
            });

        if (deviceId) {
            api.isTrustedDevice(deviceId).then(res => {
                setIsTrusted(res.data);
                if (res.data) {
                    setMode(modes.pin);
                }
            }).catch(error => {
                console.error("api.isTrustedDevice", error);
                setIsTrusted(false);
                setMode(modes.password);
            });
        }
    }, []);

    useEffect(() => {
        userNameRef.current.focus();
    }, [userNameRef]);

    const renderDeviceLockWarning = () => {
        return (
          <div>
              {t("This Device have been locked due to 3 failed login attempts. " +
                "Please login with your full credentials or contact the Administration.")}
          </div>
        );
    }

    const login = () => {
        let user;
        let pass;
        if (mode === modes.pin) {
            // password = <deviceId>+<PIN 6 digits>
            let paddedPin = _.padStart(pin, 6, "0");
            if (paddedPin.length > 6) {
                paddedPin = paddedPin.substring(0, 6);
            }
            user = "_pin";
            // pass = deviceId + pin;
            pass = deviceId + paddedPin;
        } else if (mode === modes.password) {
            user = userName.trim();
            pass = password;
        }

        if (!user || !pass) {
            return;
        }

        setLoginInProgress(true);
        auth.auth(user, pass).then(authResponse => {
                setLoginInProgress(false);
                if (authResponse === true) {
                    setLoginStatus(prevState => ({
                        ...prevState,
                        authorized: true,
                        error: null,
                        errorDescription: null,
                        token: auth.token,
                        refreshToken: auth.refreshToken,
                    }));
                    if (preventLogin) {
                        history.push("/");
                        sessionStorage.setItem("maintenance", "true");
                    }
                    window.location.reload();
                } else {
                    setLoginStatus(prevState => ({
                        ...prevState,
                        authorized: false,
                        error: auth.error,
                        errorDescription: auth.errorDescription,
                        token: null,
                        refreshToken: null,
                        statusLocked: auth.locked
                    }));
                }
            },
        ).catch(error => {
            setLoginInProgress(false);
            setLoginStatus(prevState => ({
                ...prevState,
                authorized: false,
                error: auth.error,
                errorDescription: auth.errorDescription,
                token: null,
                refreshToken: null,
                statusLocked: auth.locked
            }));
        });
    };

    const handleKeyDown = (e) => {
        if (e.keyCode == 13 && !loginInProgress) {
            if (e.target.name === "userName") {
                passwordRef.current.focus();
            } else if (e.target.name === "password" || e.target.name === "pin") {
                login();
            }
        }
    };

    const toggleShowPassword = () => {
        if (!config?.disableShowPassword) setShowPassword(!showPassword)
    }

    return (
      <div className={classes.page}>
          <Backdrop className={classes.backdrop} open={loginInProgress}>
              <CircularProgress color="inherit" size={64}/>
          </Backdrop>
          <div>
              <div className={classes.container}>
                  <div className={classes.logo}>
                      <img alt="Agyle" src="/image/agyle_logo.svg" title="Agyle"/>
                  </div>
                  <div className={classes.content}>
                      <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12}>
                              {config?.loginScreenNames?.map((text, index) => (
                                <Typography key={index} className={classes.contentText}>{text}</Typography>
                              ))}
                          </Grid>
                          {mode === modes.password && <React.Fragment>
                              <Grid item xs={12}>
                                  <TextField
                                    className={classes.inputStyle}
                                    name="userName"
                                    label={t("Username")}
                                    onChange={event => setUserName(event.target.value)}
                                    value={userName}
                                    variant="outlined"
                                    error={!userName}
                                    fullWidth
                                    autoComplete="off"
                                    onKeyDown={handleKeyDown}
                                    inputRef={userNameRef}
                                  />
                              </Grid>
                              <Grid item xs={12}>
                                  <TextField
                                    className={classes.inputStyle}
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    label={t("Password")}
                                    onChange={event => setPassword(event.target.value)}
                                    value={password}
                                    variant="outlined"
                                    error={!password}
                                    fullWidth
                                    autoComplete="off"
                                    onKeyDown={handleKeyDown}
                                    inputRef={passwordRef}
                                    InputProps={{
                                        endAdornment:
                                          !config?.disableShowPassword&&
                                          <InputAdornment position="end">
                                              <IconButton
                                                className={classes.lnkbtn}
                                                tabIndex={-1}
                                                aria-label="toggle password visibility"
                                                onClick={toggleShowPassword}
                                              >
                                                  {showPassword ? <VisibilityOff/> : <Visibility/>}
                                              </IconButton>
                                          </InputAdornment>,
                                    }}

                                  />
                              </Grid>
                          </React.Fragment>}
                          {mode === modes.pin && <Grid item xs={12}>
                              <TextField
                                className={classes.inputStyle}
                                name="pin"
                                label={t("PIN")}
                                type={showPin ? 'text' : 'password'}
                                onChange={event => setPin(event.target.value)}
                                value={pin}
                                variant="outlined"
                                error={!pin}
                                fullWidth
                                autoComplete="off"
                                autoFocus={true}
                                onKeyDown={handleKeyDown}
                                InputProps={{
                                    endAdornment:
                                      <InputAdornment position="end">
                                          <IconButton
                                            className={classes.lnkbtn}
                                            aria-label="toggle pin visibility"
                                            onClick={() => setShowPin(!showPin)}
                                          >
                                              {showPin ? <VisibilityOff/> : <Visibility/>}
                                          </IconButton>
                                      </InputAdornment>,
                                }}
                              />
                          </Grid>}
                          <Grid item xs={12}>
                              <Button
                                size="large"
                                fullWidth
                                className={classes.btn}
                                variant="contained"
                                disabled={(mode === modes.password && (!userName || !password)) ||
                                  (mode === modes.pin && (auth.locked || !pin))}
                                onClick={login}
                              >
                                  {t("Login")}
                              </Button>
                          </Grid>
                          {(isTrusted || mode === modes.pin) && <Grid item xs={12} style={{textAlign: "right"}}>
                              <Link
                                component="button"
                                variant="body2"
                                tabIndex={-1}
                                className={classes.lnkbtn}
                                onClick={() => {
                                    setMode(mode === modes.pin ? modes.password : modes.pin);
                                }}
                              >
                                  {mode === modes.pin ? t("Switch to Username/Password") : t("Switch to PIN")}
                              </Link>
                          </Grid>}
                          <Grid item xs={12}>
                              <div className={classes.error}>
                                  {loginStatus.error && !loginStatus?.statusLocked && <span>{t("Invalid username or password.")}</span>}
                                  {loginStatus?.statusLocked && <span>{renderDeviceLockWarning()}</span>}
                              </div>
                          </Grid>
                      </Grid>
                  </div>
              </div>
              <div className={classes.footer}>
                  <span>&copy;</span> 2022 Fortrus, Inc. All rights reserved
              </div>
          </div>
      </div>
    );
}

export default LoginApp;